"use client";

import "swiper/css";

import { Children, useState } from "react";

import cn from "classnames";
import type { ReactNode } from "react";
import type { Swiper as SwiperType } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import type { SwiperOptions } from "swiper/types";

import { CarouselControls } from "@/components/carouselControls/carouselControls.component";

import type { SmbHorizontalCardsVariant } from "./smbHorizontalCards.types";

// Keys match Tailwind `md` and `lg`. Groups equal the whole slides per view so
// each arrow click moves one page; the .2 on Text carousel is the peek.
const BREAKPOINTS: Record<
  SmbHorizontalCardsVariant,
  NonNullable<SwiperOptions["breakpoints"]>
> = {
  imageCarousel: {
    768: { slidesPerView: 2, slidesPerGroup: 2, spaceBetween: 24 },
    1024: { slidesPerView: 3, slidesPerGroup: 3, spaceBetween: 24 },
  },
  // Desktop renders a static grid instead of this carousel.
  productTiles: {
    768: { slidesPerView: 2, slidesPerGroup: 2, spaceBetween: 24 },
  },
  textCarousel: {
    768: { slidesPerView: 2.2, slidesPerGroup: 2, spaceBetween: 24 },
    1024: { slidesPerView: 4.2, slidesPerGroup: 4, spaceBetween: 24 },
  },
};

export function SmbHorizontalCardsCarousel({
  variant,
  ariaLabel,
  footer,
  className,
  children,
}: SmbHorizontalCardsCarouselProps) {
  const [swiper, setSwiper] = useState<SwiperType | null>(null);

  return (
    <div
      data-testid="smb-horizontal-cards-carousel"
      className={cn("flex w-full flex-col gap-8 lg:gap-12", className)}>
      <Swiper
        slidesPerView={1}
        slidesPerGroup={1}
        spaceBetween={16}
        breakpoints={BREAKPOINTS[variant]}
        onSwiper={setSwiper}
        className="w-full! [&_.swiper-slide]:h-auto!">
        {Children.toArray(children).map((child, index) => (
          <SwiperSlide key={index} className="h-auto">
            {child}
          </SwiperSlide>
        ))}
      </Swiper>
      {footer}
      <CarouselControls swiper={swiper} ariaLabel={ariaLabel} />
    </div>
  );
}

export type SmbHorizontalCardsCarouselProps = {
  variant: SmbHorizontalCardsVariant;
  /** Accessible name for the page dots. */
  ariaLabel: string;
  /** Rendered between the slides and the controls, e.g. the disclaimer. */
  footer?: ReactNode;
  className?: string;
  children: ReactNode;
};
